/**
 * [LIVE] determination for the automation editors.
 *
 * A semantics/relevance key is [LIVE] iff the SAMPLE seed
 * (serviceToDesign(SAMPLE_FLIGHT, sampleServiceFor(kind), style), surfaced by the
 * GET-one seed path / mock SAMPLE_SEED) populates it AND it is not a template-owned
 * carve-out. The mint refreshes exactly that key set, so the UI's read-only set and
 * the mint's refresh set can never diverge.
 *
 * airlineLoungePlaceID (operator's Maps lounge pin) and maxDistance (template
 * geofence radius) are template-owned even when the seed carries a value — they are
 * explicitly excluded from the live set.
 */
import type { PassDesign, PassDesignSemantics } from '@insurwiz/wallet-core';

type SemanticsKey = keyof PassDesignSemantics;
type RelevanceKey = 'locations' | 'maxDistance' | 'relevantDate' | 'expirationDate';

/** Semantics keys that are template-owned even if seeded. */
const SEMANTICS_TEMPLATE_OWNED: ReadonlySet<SemanticsKey> = new Set<SemanticsKey>([
  'airlineLoungePlaceID',
]);

/** Semantics keys the seed populates (= [LIVE]), minus template-owned carve-outs. */
export function liveSemanticsKeys(seed: PassDesign): SemanticsKey[] {
  const semantics = seed.semantics;
  if (semantics === undefined) return [];
  return (Object.keys(semantics) as SemanticsKey[]).filter(
    (key) => semantics[key] !== undefined && !SEMANTICS_TEMPLATE_OWNED.has(key),
  );
}

/** Relevance keys the seed populates (= [LIVE]). maxDistance is always template-owned. */
export function liveRelevanceKeys(seed: PassDesign): RelevanceKey[] {
  const relevance = seed.relevance;
  if (relevance === undefined) return [];
  const keys: RelevanceKey[] = [];
  if (relevance.locations !== undefined) keys.push('locations');
  if (relevance.relevantDate !== undefined) keys.push('relevantDate');
  if (relevance.expirationDate !== undefined) keys.push('expirationDate');
  return keys; // maxDistance intentionally excluded (template radius)
}
